/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ArchiveVariantDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-10-08
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-10-08   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import CustomCheckBoxV2 from "../common/checkBox/CustomCheckBoxV2";
import VlcCardHeader from "../vlc/cards/VlcCardHeader";
import VlcCardContent from "../vlc/cards/VlcCardContent";
import { vlcCardStyles, vlcComponentStyle } from "../vlc/config/vlcConfig";

function ArchiveVariantDialog({
  open,
  onClose,
  workflowOption,
  variantDetailsSelected = [],
  selectedVariantsToArchive = [],
  setSelectedVariantsToArchive,
  onClickSuccess,
}) {
  if (!open) return null;

  const handleToggleVariant = (variantId) => {
    if (selectedVariantsToArchive.includes(variantId)) {
      setSelectedVariantsToArchive(selectedVariantsToArchive.filter((id) => id !== variantId));
    } else {
      setSelectedVariantsToArchive([...selectedVariantsToArchive, variantId]);
    }
  };

  const handleCheckboxChange = (variantId, checked) => {
    if (checked) {
      setSelectedVariantsToArchive([...selectedVariantsToArchive, variantId]);
    } else {
      setSelectedVariantsToArchive(selectedVariantsToArchive.filter((id) => id !== variantId));
    }
  };

  const filteredVariants = (Array.isArray(variantDetailsSelected) ? variantDetailsSelected : []).filter(
    (variant) => (workflowOption === "archive" ? variant.archive_status !== "ARCHIVED" : variant.archive_status === "ARCHIVED")
  );

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={onClose}
      title={workflowOption === "archive" ? "Archive Variant" : "Retrieve Variant"}
      dialogRadius="0rem"
      requiresDefaultPaddingForContent={false}
      headerSlot={<></>}
      successButtonName="Confirm selection"
      rejectButtonName="Cancel"
      actionButtonStyle="continuous"
      onClickSuccess={onClickSuccess}
      onClickReject={onClose}
    >
      <Box sx={{ width: "500px", maxWidth: "100%" }}>
        <Card
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "400px",
            boxShadow: "none",
            borderRadius: 0,
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={workflowOption === "archive" ? "Archive Variant" : "Retrieve Variant"}
                showRecreateIcon={false}
              />
            }
          />
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {filteredVariants.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No variants available to {workflowOption}
                </Typography>
              </Box>
            ) : (
              filteredVariants.map((variant) => (
                <VlcCardContent
                  key={variant.variant_id}
                  onClick={() => handleToggleVariant(variant.variant_id)}
                  isSelected={selectedVariantsToArchive.includes(variant.variant_id)}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      p: 1,
                    }}
                  >
                    <CustomCheckBoxV2
                      label={variant.name}
                      value={selectedVariantsToArchive.includes(variant.variant_id)}
                      onChange={(e) => handleCheckboxChange(variant.variant_id, e.target.checked)}
                    />
                  </Box>
                </VlcCardContent>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </CustomDialogBoxV2>
  );
}

ArchiveVariantDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  workflowOption: PropTypes.string,
  variantDetailsSelected: PropTypes.array,
  selectedVariantsToArchive: PropTypes.array,
  setSelectedVariantsToArchive: PropTypes.func,
  onClickSuccess: PropTypes.func,
};

export default ArchiveVariantDialog;
